import { defineStore } from 'pinia'

import SensorResource from '@/api/resources/sensor'
import { IMixingCycleSettings } from '@/interfaces/api/mixing-cycle'
import type { IFetchSensorReadingsParams } from '@/interfaces/api/register'
import type { ISensor } from '@/interfaces/entities/sensor'
import { SensorType } from '@/interfaces/enums.ts'
import type { IEnergySensorReading } from '@/interfaces/stores/register'
import type { IRegisterChartSensorData } from '@/utils/components/register-chart'
import { fetchSensorReadingsByType } from '@/utils/dashboard/fetch-sensor-readings-by-type'
import { energyReadingMatchesDateRange, formatPeriodDate } from '@/utils/date/date-time'

// Interfaces para análise
interface AnalysisState {
  isExpanded: boolean
  isMinimized: boolean
  isSelectorOpen: boolean
  isConfiguratorOpen: boolean
  sensorData: any
  startDate: Date | null
  endDate: Date | null
  selectedAnalyses: string[]
  parameters: any
  results: any
  annotations: any[]
  secondarySensorId: number | null
  secondarySensorData: IRegisterChartSensorData | null
  correlationSensors: Array<{ sensorId: number; data: IRegisterChartSensorData | null }>
  isLoadingCorrelation: boolean
  correlationError: string | null
}

interface AnalysisConfig {
  window_period: string
  min_cycle_duration: number
  max_cycle_duration: number
  change_percentile: number
  min_data_points: number
  smoothing_factor: number
  use_neighbor_average: boolean
  rate_multiplier: number
}

export const useRegisterStore = defineStore({
  id: 'register',
  persist: true,

  state: () => ({
    selectedSensor: {} as ISensor,
    energyReadings: [] as IEnergySensorReading[],
    sensors: [] as ISensor[],
    /** Período do filtro da barra de busca (nova consulta). Cada gráfico usa o próprio start_date/end_date do reading. */
    period: [] as Date[],
    nextIndex: 1,
    sensorAnalyses: {} as Record<number, AnalysisState>,
    advancedSettings: {
      resolution: '30s',
      aggregation: 'mean',
    },
    defaultCycleConfig: {
      window_period: '1m',
      min_cycle_duration: 60,
      max_cycle_duration: 350,
      change_percentile: 70,
      min_data_points: 10,
      smoothing_factor: 1.0,
      use_neighbor_average: true,
      rate_multiplier: 2.0,
    } as AnalysisConfig,
  }),

  getters: {
    getPeriodFilter: (state) => state.period,
    // Filtra leituras malformadas (ex.: estado persistido antigo sem `sensor`)
    // para não quebrar a renderização do template/gráfico.
    getEnergyReading: (state) => state.energyReadings.filter((r) => r && r.sensor),
    listSensors: (state) => state.sensors,
    getAdvancedSettings: (state) => ({
      resolution: state.advancedSettings?.resolution || '30s',
      aggregation: state.advancedSettings?.aggregation || 'mean',
    }),

    isDefaultAdvancedSettings: (state) => {
      const settings = state.advancedSettings
      return settings?.resolution === '30s' && settings?.aggregation === 'mean'
    },

    getSensorAnalysis:
      (state) =>
        (sensorIndex: number): AnalysisState | null => {
          return state.sensorAnalyses[sensorIndex] || null
        },

    getAnalysisParameters: (state) => (sensorIndex: number) => {
      const analysis = state.sensorAnalyses[sensorIndex]
      const parameters = analysis?.parameters || state.defaultCycleConfig
      return parameters
    },

    getAnalysisResults: (state) => (sensorIndex: number) => {
      const analysis = state.sensorAnalyses[sensorIndex]
      const results = analysis?.results || null

      return results
    },

    getAnalysisAnnotations: (state) => (sensorIndex: number) => {
      const analysis = state.sensorAnalyses[sensorIndex]
      const annotations = analysis?.annotations || []

      return annotations
    },

    isAnalysisExpanded:
      (state) =>
        (sensorIndex: number): boolean => {
          const analysis = state.sensorAnalyses[sensorIndex]
          return analysis?.isExpanded || false
        },

    isAnalysisMinimized:
      (state) =>
        (sensorIndex: number): boolean => {
          const analysis = state.sensorAnalyses[sensorIndex]
          return analysis?.isMinimized || false
        },

    isSelectorOpen:
      (state) =>
        (sensorIndex: number): boolean => {
          const analysis = state.sensorAnalyses[sensorIndex]
          return analysis?.isSelectorOpen || false
        },

    isConfiguratorOpen:
      (state) =>
        (sensorIndex: number): boolean => {
          const analysis = state.sensorAnalyses[sensorIndex]
          return analysis?.isConfiguratorOpen || false
        },

    getSelectedAnalysisType:
      (state) =>
        (sensorIndex: number): string | null => {
          const analysis = state.sensorAnalyses[sensorIndex]
          return analysis?.selectedAnalyses?.[0] || null
        },

    getCorrelationSensors: (state) => (sensorIndex: number) => {
      const analysis = state.sensorAnalyses[sensorIndex]
      if (!analysis || analysis.selectedAnalyses?.[0] !== 'correlation_analysis') {
        return []
      }

      const list = Array.isArray(analysis.correlationSensors) ? analysis.correlationSensors : []

      if (list.length === 0 && analysis.secondarySensorId != null) {
        return [{ sensorId: analysis.secondarySensorId, data: analysis.secondarySensorData ?? null }]
      }
      return list
    },

    getCorrelationSecondaryData:
      (state) =>
      (sensorIndex: number): IRegisterChartSensorData | null => {
        const analysis = state.sensorAnalyses[sensorIndex]
        if (!analysis || analysis.selectedAnalyses?.[0] !== 'correlation_analysis') {
          return null
        }
        const list = Array.isArray(analysis.correlationSensors) ? analysis.correlationSensors : []
        if (list.length === 0 && analysis.secondarySensorId != null) {
          return analysis.secondarySensorData ?? null
        }
        return list[0]?.data ?? null
      },

    getCorrelationSecondarySensorId:
      (state) =>
      (sensorIndex: number): number | null => {
        const analysis = state.sensorAnalyses[sensorIndex]
        if (!analysis || analysis.selectedAnalyses?.[0] !== 'correlation_analysis') {
          return null
        }
        const list = Array.isArray(analysis.correlationSensors) ? analysis.correlationSensors : []
        if (list.length === 0 && analysis.secondarySensorId != null) {
          return analysis.secondarySensorId
        }
        return list[0]?.sensorId ?? null
      },

    isCorrelationSensorAdded:
      (state) =>
      (sensorIndex: number, sensorId: number): boolean => {
        const analysis = state.sensorAnalyses[sensorIndex]
        if (!analysis) return false
        if (String(analysis.secondarySensorId ?? '') === String(sensorId)) return true
        return (
          Array.isArray(analysis.correlationSensors) &&
          analysis.correlationSensors.some((entry) => String(entry.sensorId) === String(sensorId))
        )
      },

    isLoadingCorrelation:
      (state) =>
        (sensorIndex: number): boolean => {
          return state.sensorAnalyses[sensorIndex]?.isLoadingCorrelation || false
        },

    getCorrelationError:
      (state) =>
        (sensorIndex: number): string | null => {
          return state.sensorAnalyses[sensorIndex]?.correlationError || null
        },
  },

  actions: {
    async fetchSensors() {
      try {
        this.sensors = await SensorResource.getAllSensors()
      } catch {
        // Erro ao buscar sensores
      }
    },

    async fetchSensorReadings(payload: IFetchSensorReadingsParams) {
      const existing = this.energyReadings.find(
        (r) => r.sensor.id === payload.sensor.id && energyReadingMatchesDateRange(r, payload.dateRange),
      )
      if (existing) return

      try {
        let readings: any = null
        const sensor_type = payload.sensor.sensor_type

        // Usar estado direto para evitar valor desatualizado (ex.: persist rehydration)
        const resolution = this.advancedSettings?.resolution || '30s'
        const aggregation = this.advancedSettings?.aggregation || 'mean'

        const newPayload: {
          sensorId: number
          dateRange: string
          resolution: string
          aggregation: string
        } = {
          sensorId: payload.sensor.id,
          dateRange: payload.dateRange,
          resolution,
          aggregation,
        }

        if (sensor_type === SensorType.ELECTRICITY) {
          readings = await SensorResource.getEnergySensorReadings(newPayload)
        } else if (sensor_type === SensorType.DOOR_OPEN) {
          readings = await SensorResource.getOpenSensorReadings(newPayload)
        } else if (sensor_type === SensorType.TEMPERATURE) {
          readings = await SensorResource.getTemperatureSensorReadings(newPayload)
        } else {
          console.warn(`Tipo de sensor sem endpoint de leituras: ${sensor_type}`)
          return
        }

        // Evita montar uma leitura inválida quando a API não retorna dados
        if (!readings || !readings.sensor) {
          console.warn(`Sem leituras para o sensor ${payload.sensor.id} (${sensor_type})`)
          return
        }

        const energyReadings: IEnergySensorReading = {
          sensor: readings.sensor,
          start_date: readings.start_date,
          end_date: readings.end_date,
          readings: readings.readings,
          index: this.nextIndex,
        }

        this.energyReadings.push(energyReadings)
        this.nextIndex++
      } catch {
        // Erro ao buscar leituras
      }
    },

    setSensor(sensor: ISensor) {
      this.selectedSensor = sensor
    },

    setPeriod(period: Date[]) {
      this.period = period
    },

    setAdvancedSettings(settings: { resolution: string; aggregation: string }) {
      this.advancedSettings = {
        resolution: settings.resolution || '30s',
        aggregation: settings.aggregation || 'mean',
      }
    },

    forceUpdateAdvancedSettings(settings: { resolution: string; aggregation: string }) {
      const resolution = String(settings?.resolution ?? '30s').trim()
      const aggregation = String(settings?.aggregation ?? 'mean').trim()
      this.$patch({
        advancedSettings: {
          resolution: resolution || '30s',
          aggregation: aggregation || 'mean',
        },
      })
    },

    initializeAdvancedSettings() {
      if (!this.advancedSettings || !this.advancedSettings.resolution || !this.advancedSettings.aggregation) {
        this.advancedSettings = {
          resolution: '30s',
          aggregation: 'mean',
        }
      }
    },

    resetAdvancedSettings() {
      this.advancedSettings = {
        resolution: '30s',
        aggregation: 'mean',
      }
    },

    removeSensor(index: number) {
      this.clearAnalysis(index)
      this.energyReadings = this.energyReadings.filter((energyReading) => energyReading.index !== index)
    },

    initializeAnalysis(sensorIndex: number, sensorData: any) {
      const existingAnalysis = this.sensorAnalyses[sensorIndex]

      if (!existingAnalysis) {
        this.sensorAnalyses[sensorIndex] = {
          isExpanded: true,
          isMinimized: false,
          isSelectorOpen: true,
          isConfiguratorOpen: false,
          sensorData: sensorData,
          startDate: new Date(sensorData.start_date),
          endDate: new Date(sensorData.end_date),
          selectedAnalyses: [],
          parameters: { ...this.defaultCycleConfig },
          results: null,
          annotations: [],
          secondarySensorId: null,
          secondarySensorData: null,
          correlationSensors: [],
          isLoadingCorrelation: false,
          correlationError: null,
        }
      } else {
        existingAnalysis.isExpanded = true
        existingAnalysis.isMinimized = false
        existingAnalysis.isSelectorOpen = true
        existingAnalysis.isConfiguratorOpen = false
        if (existingAnalysis.correlationSensors === undefined) {
          existingAnalysis.correlationSensors = []
          if (existingAnalysis.secondarySensorId === undefined) {
            existingAnalysis.secondarySensorId = null
            existingAnalysis.secondarySensorData = null
            existingAnalysis.isLoadingCorrelation = false
            existingAnalysis.correlationError = null
          }
        }
        this.clearCorrelationSecondary(sensorIndex)
      }
    },

    toggleAnalysisMinimization(sensorIndex: number) {
      const analysis = this.sensorAnalyses[sensorIndex]
      if (analysis) {
        analysis.isMinimized = !analysis.isMinimized
      }
    },

    proceedToAnalysis(sensorIndex: number, analysisType: string) {
      const analysis = this.sensorAnalyses[sensorIndex]
      if (analysis) {
        analysis.selectedAnalyses = [analysisType]
        analysis.isSelectorOpen = false
        analysis.isConfiguratorOpen = true
        analysis.isMinimized = false

        if (analysisType === 'correlation_analysis') {
          analysis.parameters = { secondarySensorId: null }
          this.clearCorrelationSecondary(sensorIndex)
        }
      }
    },

    backToSelector(sensorIndex: number) {
      const analysis = this.sensorAnalyses[sensorIndex]
      if (analysis) {
        analysis.isConfiguratorOpen = false
        analysis.isSelectorOpen = true
        analysis.isMinimized = false
        this.clearCorrelationSecondary(sensorIndex)
      }
    },

    clearCorrelationSecondary(sensorIndex: number) {
      const analysis = this.sensorAnalyses[sensorIndex]
      if (!analysis) return

      analysis.secondarySensorId = null
      analysis.secondarySensorData = null
      analysis.correlationSensors = []
      analysis.isLoadingCorrelation = false
      analysis.correlationError = null
    },

    async addCorrelationSensor(sensorIndex: number, sensor: ISensor) {
      const analysis = this.sensorAnalyses[sensorIndex]
      if (!analysis) return
      if (this.isCorrelationSensorAdded(sensorIndex, sensor.id)) return

      if (!Array.isArray(analysis.correlationSensors)) analysis.correlationSensors = []
      analysis.correlationSensors.push({ sensorId: sensor.id, data: null })
      if (analysis.secondarySensorId == null) {
        analysis.secondarySensorId = sensor.id
      }

      await this.fetchCorrelationSensorReadings(sensorIndex, sensor)
    },

    removeCorrelationSensor(sensorIndex: number, sensorId: number) {
      const analysis = this.sensorAnalyses[sensorIndex]
      if (!analysis) return

      const removed = (analysis.correlationSensors = Array.isArray(analysis.correlationSensors)
        ? analysis.correlationSensors.filter(
            (entry) => String(entry.sensorId) !== String(sensorId),
          )
        : [])
      
      if (String(analysis.secondarySensorId ?? '') === String(sensorId)) {
        analysis.secondarySensorId = removed[0]?.sensorId ?? null
        analysis.secondarySensorData = removed[0]?.data ?? null
      }
      if (removed.length === 0) {
        analysis.secondarySensorId = null
        analysis.secondarySensorData = null
      }
    },

    async fetchCorrelationSensorReadings(sensorIndex: number, secondarySensor: ISensor) {
      const analysis = this.sensorAnalyses[sensorIndex]
      if (!analysis?.startDate || !analysis?.endDate) return

      analysis.isLoadingCorrelation = true
      analysis.correlationError = null

      try {
        const dateRange = formatPeriodDate([
          analysis.startDate instanceof Date ? analysis.startDate : new Date(analysis.startDate),
          analysis.endDate instanceof Date ? analysis.endDate : new Date(analysis.endDate),
        ])
        const resolution = this.advancedSettings?.resolution || '30s'
        const aggregation = this.advancedSettings?.aggregation || 'mean'

        const sensorData = await fetchSensorReadingsByType({
          sensor: secondarySensor,
          dateRange,
          resolution,
          aggregation,
        })

        const entry = (Array.isArray(analysis.correlationSensors)
          ? analysis.correlationSensors
          : []
        ).find((item) => item.sensorId === secondarySensor.id)
        if (entry) {
          entry.data = sensorData
        }

        if (analysis.secondarySensorId === secondarySensor.id) {
          analysis.secondarySensorData = sensorData
        }

        if (!sensorData) {
          analysis.correlationError = 'fetch_failed'
        }
      } catch {
        analysis.correlationError = 'fetch_failed'
      } finally {
        analysis.isLoadingCorrelation = false
      }
    },

    fetchCorrelationSecondaryReadings(sensorIndex: number, secondarySensor: ISensor) {
      return this.fetchCorrelationSensorReadings(sensorIndex, secondarySensor)
    },

    updateAnalysisParameters(sensorIndex: number, parameters: any) {
      const analysis = this.sensorAnalyses[sensorIndex]
      if (analysis) {
        analysis.parameters = { ...analysis.parameters, ...parameters }
      }
    },

    setAnalysisResults(sensorIndex: number, results: any) {
      const analysis = this.sensorAnalyses[sensorIndex]
      if (analysis) {
        analysis.results = results
      }
    },

    addAnalysisAnnotations(sensorIndex: number, annotations: any[]) {
      const analysis = this.sensorAnalyses[sensorIndex]
      if (analysis) {
        analysis.annotations = [...annotations]
      }
    },

    clearAnalysisAnnotations(sensorIndex: number) {
      const analysis = this.sensorAnalyses[sensorIndex]
      if (analysis) {
        analysis.annotations = []
      }
    },

    clearAnalysis(sensorIndex: number) {
      delete this.sensorAnalyses[sensorIndex]
    },

    // API actions para carregar/salvar parâmetros
    async loadAnalysisParameters(sensorIndex: number) {
      try {
        const analysis = this.sensorAnalyses[sensorIndex]
        if (!analysis) return

        // Carregar configurações da API
        const settings = await this.loadMixingCycleSettings(analysis.sensorData.sensor.id)

        if (settings) {
          // Combinar com configurações padrão
          analysis.parameters = {
            ...this.defaultCycleConfig,
            ...settings,
          }
        } else {
          // Usar configurações padrão se não houver configurações salvas
          analysis.parameters = { ...this.defaultCycleConfig }
        }
      } catch {
        const analysis = this.sensorAnalyses[sensorIndex]
        if (analysis) {
          analysis.parameters = { ...this.defaultCycleConfig }
        }
      }
    },

    async saveAnalysisParameters(sensorIndex: number) {
      try {
        const analysis = this.sensorAnalyses[sensorIndex]
        if (analysis) {
          // Parâmetros persistidos via análise
        }
      } catch {
        // Erro ao salvar parâmetros
      }
    },

    async executeAnalysis(sensorIndex: number, customParameters?: any) {
      let analysis = this.sensorAnalyses[sensorIndex]

      try {
        if (!analysis) return

        if (!analysis.sensorData || !analysis.sensorData.readings) {
          throw new Error('Dados do sensor inválidos')
        }

        const parametersToUse = customParameters || analysis.parameters

        this.clearAnalysisAnnotations(sensorIndex)

        const { CycleAnalysisService } = await import('@/services/cycle-analysis.service')

        const serviceParams = {
          sensor: analysis.sensorData,
          start_date: analysis.startDate instanceof Date ? analysis.startDate : new Date(analysis.startDate),
          end_date: analysis.endDate instanceof Date ? analysis.endDate : new Date(analysis.endDate),
          min_cycle_duration: parametersToUse.min_cycle_duration,
          max_cycle_duration: parametersToUse.max_cycle_duration,
          change_percentile: parametersToUse.change_percentile,
          rate_multiplier: parametersToUse.rate_multiplier,
          min_data_points: parametersToUse.min_data_points,
          smoothing_factor: parametersToUse.smoothing_factor,
          use_neighbor_average: parametersToUse.use_neighbor_average,
          window_period: parametersToUse.window_period,
        }

        const service = new CycleAnalysisService(serviceParams)
        const result = await service.call()

        this.setAnalysisResults(sensorIndex, result)

        if (result.periods && result.periods.length > 0) {
          const annotations = result.periods.map((period, index) => {
            const annotation = {
              id: `cycle-${sensorIndex}-${index}`,
              type: 'cycle',
              start: new Date(period.start),
              end: new Date(period.end),
              label: `Ciclo ${index + 1} (${Math.round(period.duration)}s)`,
              color: '#Ca8A04',
            }

            return annotation
          })

          this.addAnalysisAnnotations(sensorIndex, annotations)
        }
      } catch {
        // Erro ao executar análise
      }
    },

    loadMixingCycleSettings(sensorId: number) {
      try {
        const settings = this.energyReadings.find((reading) => reading.sensor.id === sensorId)?.sensor.mixing_cycle_settings
        return settings
      } catch {
        return null
      }
    },

    async saveMixingCycleSettings(sensorId: number, settings: Partial<IMixingCycleSettings>) {
      try {
        await SensorResource.updateMixingCycleSettings(sensorId, settings)
        return true
      } catch {
        return false
      }
    },

    clearAdvancedSettingsPersistence() {
      this.advancedSettings = {
        resolution: '30s',
        aggregation: 'mean',
      }
    },
  },
})
